import React, { useState, useContext, useEffect, useRef } from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { useNode, useEditor } from '@craftjs/core';

import MyButton from '@common/components/Button/MyButton';
import SettingsWrapper from '@common/components/SettingsWrapper/SettingsWrapper';
import SettingsRow from '@common/components/SettingsWrapper/SettingsRow';
import {
  EDITOR_RESOLVERS,
  INIT_PLACEHOLDER_MODAL_STATE,
} from '@common/constants';

import TextStyle from '@resolvers/tools/TextStyle/TextStyle';
import DisplayStyle from '@resolvers/tools/DisplayStyle/DisplayStyle';
import FontSizeStyle from '@resolvers/tools/FontSizeStyle/FontSizeStyle';
import PaddingStyle from '@resolvers/tools/PaddingStyle/PaddingStyle';

import { Typography } from '@mui/material';

import PlaceholderModal from './components/PlaceholderModal';
import { getParentSrc } from '@common/helpers/helpers';
import { get } from 'lodash';
import { useTemplateDetailsStore } from '@store/templateDetailsStore';

const Placeholder = ({
  fieldName,
  placeholder,
  display,
  fontSize,
  textAlign,
  fontWeight,
  textDecoration,
  fontItalic,
  background,
  fontColor,
  paddingLeft,
  paddingRight,
  paddingTop,
  paddingBottom,
  ifAtt,
}) => {
  const { t } = useTranslation();
  const ref = useRef(null);

  const { actions } = useEditor();

  const {
    connectors: { connect, drag },
    actions: { setCustom },
  } = useNode();

  useEffect(() => {
    if (ref?.current && connect) {
      connect(drag(ref.current));
    }
  }, [connect]);

  useEffect(() => {
    actions.history.ignore(() => {
      setCustom((props) => {
        props.displayName = t('placeholder');
      });
    });
  }, [setCustom, t]);

  return (
    <p
      ref={ref}
      role='presentation'
      style={{
        cursor: 'grab',
        margin: '0',
        display: display,
        fontSize: `${fontSize}px`,
        textAlign: `${textAlign}`,
        fontWeight: `${fontWeight ? 'bold' : '100'}`,
        textDecoration: `${textDecoration ? 'underline' : 'none'}`,
        fontStyle: `${fontItalic ? 'italic' : 'normal'}`,
        color: `${fontColor}`,
        background: `${background}`,
        fontFamily: 'sans-serif',
        paddingLeft: `${paddingLeft}px`,
        paddingRight: `${paddingRight}px`,
        paddingTop: `${paddingTop}px`,
        paddingBottom: `${paddingBottom}px`,
        width: 'fit-content',
      }}
    >
      <span>{placeholder || t('placeholder')}</span>
    </p>
  );
};

const PlaceholderConfigPanel = () => {
  const { source } = useTemplateDetailsStore();
  const [showModal, setShowModal] = useState(INIT_PLACEHOLDER_MODAL_STATE);

  const { query } = useEditor();

  const {
    actions: { setProp },
    fieldName,
    parentId,
    currentFormula,
  } = useNode(({ data: { props, parent } }) => {
    return {
      fieldName: props?.fieldName,
      placeholder: props?.placeholder,
      parentId: parent,
      currentFormula: props?.currentFormula,
    };
  });

  const { t } = useTranslation();

  const changeText = (newValue) => {
    setProp((props) => {
      props.fieldName = newValue;
    }, 500);
  };

  const onCalcSave = (value) => {
    setProp((props) => {
      props.currentFormula = value;
    }, 500);
  };

  const onSelectSourceClick = (e) => {
    const { parentSource, parentListSrc } = getParentSrc({ query, parentId });

    setShowModal({
      show: true,
      listSource: parentSource,
      parentListSrc,
      parent: '',
      showRootFields: typeof parentSource === 'undefined',
      action: e?.currentTarget?.name,
    });
  };

  useEffect(() => {
    if (fieldName)
      setProp((props) => {
        props.placeholder = '{{' + fieldName + '}}';
      });
  }, [fieldName, setProp]);

  return (
    <SettingsWrapper>
      <PlaceholderModal
        currentValue={fieldName}
        currentFormula={currentFormula}
        documentJson={
          showModal?.parentListSrc
            ? get(source?.structure || {}, showModal.parentListSrc)
            : source?.structure || {}
        }
        showModal={showModal}
        setShowModal={setShowModal}
        changeTextFunc={changeText}
        onCalcSave={onCalcSave}
        setProp={setProp}
      />

      <SettingsRow sx={{ mb: 1 }}>
        <Typography>{`${t('property_name')}: ${fieldName}`}</Typography>
      </SettingsRow>
      <SettingsRow sx={{ mb: 3 }}>
        <MyButton name='source' onClick={onSelectSourceClick}>
          {t('select_source')}
        </MyButton>
      </SettingsRow>
      <SettingsRow>
        <TextStyle />
      </SettingsRow>
      <SettingsRow>
        <FontSizeStyle />
      </SettingsRow>
      <SettingsRow>
        <PaddingStyle />
      </SettingsRow>
      <SettingsRow>
        <DisplayStyle sx={{ mb: 3 }} />
      </SettingsRow>
    </SettingsWrapper>
  );
};

export const PlaceholderDefaultProps = {
  fieldName: '',
  placeholder: '',
  display: 'block',
  isInList: 'def',
  fontSize: 12,
  paddingLeft: '5',
  paddingRight: '5',
  paddingTop: '5',
  paddingBottom: '5',
  isNumber: false,
  ifAtt: '',
  isDeletable: true,
};

Placeholder.defaultProps = PlaceholderDefaultProps;

Placeholder.propTypes = {
  fieldName: PropTypes.string,
  placeholder: PropTypes.string,
  display: PropTypes.string,
  isInList: PropTypes.string,
  fontSize: PropTypes.number,
  paddingLeft: PropTypes.string,
  paddingRight: PropTypes.string,
  paddingTop: PropTypes.string,
  paddingBottom: PropTypes.string,
  isNumber: PropTypes.bool,
  ifAtt: PropTypes.string,
};

Placeholder.craft = {
  displayName: EDITOR_RESOLVERS.COMMON.PLACEHOLDER,
  name: EDITOR_RESOLVERS.COMMON.PLACEHOLDER,
  props: PlaceholderDefaultProps,
  related: { settings: PlaceholderConfigPanel },
  rules: {
    canMoveIn: () => false,
  },
};

export default Placeholder;
